import type { GameHostCapabilities } from '@puzzlemania/core';
import { getPlatformCapabilities } from '../../platforms/capabilities';
import { createHostShare } from './share';
import { createHostFullscreen, hostAllows } from '../lib/fullscreen';

/**
 * Module 41 (WP-41.6) — the bridge between what a *platform* is (Module 42's
 * profile registry) and what a *game* is allowed to offer (`IGameContext.host`).
 *
 * ## Why this is one function and not two conversions
 *
 * Both composition roots — the main app's `composeServices` and the embed's
 * `composeEmbedServices` — need the same mapping, and the two are otherwise
 * deliberately independent files (the portal must never import the main root;
 * that inheritance is what put Supabase in the embed's bundle before WP-41.5).
 * Duplicating the mapping would mean a capability added to the registry reaches
 * one platform's games and not the other's, which is the exact failure this
 * framework exists to prevent — and it would look fine in review.
 *
 * ## Why it is a projection and not a pass-through
 *
 * `RuntimePlatformCapabilities` is a superset: it also carries `hasIap`,
 * `hasProgression`, `hasDailyRewards`, `locales`, `orientation` and
 * `displayName`, none of which a game has any business reading. Handing the
 * whole record to games would make every one of those a de-facto part of the
 * frozen `packages/core` contract, and `displayName` in particular is a host
 * identity a game could branch on. Only the capabilities games act on cross
 * the boundary — `hasIap` joined that set for the CrazyGames ad-only Shop
 * (below `canShop` is true on both `web` and `crazygames`, but only `web` has
 * real purchases; a game needs the distinction to decide which Shop UI to
 * open, not which platform it is on).
 *
 * `offerRewardedAd` is **not** derived from `hasAds` here. Ads need a live
 * controller (WP-41.10), not a boolean, so the portal root supplies the function
 * itself; a host with `hasAds: true` and no controller correctly offers nothing.
 */
export function gameHostFromPlatform(
  overrides: Partial<GameHostCapabilities> = {},
): GameHostCapabilities {
  const caps = getPlatformCapabilities();
  return {
    canExit: caps.canExit,
    canShop: caps.canShop,
    showGems: caps.showGems,
    canOpenLegal: caps.canOpenLegal,
    // From `hasIap`, not `canShop`: the registry already states whether
    // real-money purchases exist at all, and that is the question a restore
    // control depends on.
    canRestorePurchases: caps.hasIap,
    hasIap: caps.hasIap,
    // Module 79 (WP-79.8) — present only where the profile configures it. The
    // default link is the table's `'*'`; the embed overrides this with its
    // host adapter's pick, since one Bridge build serves many portals.
    ...(caps.share ? { share: createHostShare(caps.share, (links) => links['*'] ?? null) } : {}),
    // The Top Bar's full-screen toggle, where the profile permits it for a
    // single host ('*'). The embed decides per portal and overrides this.
    ...withFullscreen(hostAllows(caps.fullscreenButton?.hosts, undefined)),
    ...overrides,
  };
}

/** `{ fullscreen }` when permitted and the browser allows it, else nothing. */
export function withFullscreen(permitted: boolean): Pick<GameHostCapabilities, 'fullscreen'> {
  const fullscreen = createHostFullscreen(permitted);
  return fullscreen ? { fullscreen } : {};
}
